import { create } from 'zustand'
import { format, subDays, addDays, startOfDay, startOfMonth, addMonths, subMonths, isAfter, isToday, isSameWeek } from 'date-fns'
import i18next from 'i18next'
import { logDev, errorDev } from '@/utils/env'
import DiaryService from '@/services/DiaryService'
import { useAuthStore } from '@/stores/authStore'
import { useMessageStore } from '@/stores/messageStore'
import notifyApiError from '@/utils/notifyApiError'
import { PCE } from '@/utils/constants'
import { SYMPTOM_KEYS, DEFAULT_SYMPTOM, emptyStats, toDbValue, fromDbValue } from '@/components/Diary/symptomConfig'
import type { SymptomKey, SymptomStats } from '@/components/Diary/symptomConfig'

// ── Types ────────────────────────────────────────────────────────────

export interface DiaryEntry {
  date: string // YYYY-MM-DD
  symptom: SymptomKey
  createdAt?: string
  updatedAt?: string
  triptanUsed?: boolean
  painkillerUsed?: boolean
  impactScore?: number | null
  notes?: string
}

type NavDirection = 'prev' | 'next'
type ActiveTab = 'calendar' | 'stats'

interface WeekStats {
  filled: number
  total: number
}

interface MedicationWarning {
  triptanDays: number
  painkillerDays: number
  triptanWarning: boolean
  painkillerWarning: boolean
}

interface MonthStats extends SymptomStats {
  triptanDays: number
  painkillerDays: number
  totalImpact: number
  impactDays: number
  avgImpact: number
}

interface SymptomAvailability {
  key: SymptomKey
  enabled: boolean
  disabledReason: string | null
}

export interface DiaryData {
  // Data
  entries: Record<string, DiaryEntry>
  isLoading: boolean
  isLoaded: boolean
  /** Set when the entries could not be fetched. Blocks the calendar and the
   *  statistics: an empty day counts as complaint-free, so rendering them on
   *  unloaded data would claim the patient had no symptoms at all. A flag, not
   *  a message, so a missing translation can never reopen that hole. */
  loadFailed: boolean

  // Navigation
  selectedDate: Date
  currentStep: number
  showSuccess: boolean
  showCalendar: boolean
  navDirection: NavDirection | null

  // Tabs & calendar
  activeTab: ActiveTab
  calendarMonth: Date
  calendarSelectedDay: Date | null

  // Editing
  isEditing: boolean
  saveError: string | null
  /** A day save is in flight. Note saves wait for it, otherwise a late day save overwrites the note. */
  isSavingEntry: boolean

  // Form fields
  formSymptom: SymptomKey | null
  formTriptanUsed: boolean
  formPainkillerUsed: boolean
  formImpactScore: number | null
  formNotes: string

  // Computed stats
  periodStats: SymptomStats
  weekStats: WeekStats
  medicationWarning: MedicationWarning
}

export interface DiaryActions {
  fetchEntries: () => Promise<void>
  retryFetch: () => Promise<void>
  setSelectedDate: (date: Date) => void
  navigateDay: (direction: NavDirection) => void
  navigateWeek: (direction: NavDirection) => void
  setFormSymptom: (symptom: SymptomKey) => void
  setFormTriptanUsed: (val: boolean) => void
  setFormPainkillerUsed: (val: boolean) => void
  setFormImpactScore: (score: number | null) => void
  setFormNotes: (notes: string) => void
  setCurrentStep: (step: number) => void
  setShowCalendar: (val: boolean) => void
  setActiveTab: (tab: ActiveTab) => void
  setCalendarMonth: (date: Date) => void
  navigateCalendarMonth: (direction: NavDirection) => void
  selectCalendarDay: (date: Date) => void
  saveEntry: () => Promise<void>
  saveNotes: (notes: string) => Promise<void>
  cancelEditing: () => void
  startEditing: () => void
  reset: () => void
  resetForm: () => void
  getEntryForDate: (date: Date) => DiaryEntry | null
  isDateFilled: (date: Date) => boolean
  isFutureDate: (date: Date) => boolean
  isSelectedDateToday: () => boolean
}

export type DiaryState = DiaryData & DiaryActions

const PCE_MAPPING: Record<string, string> = {
  [PCE.DIARY_INVALID_CATEGORY]: 'pce_diary_invalid_category',
  [PCE.DIARY_INVALID_DATE_FORMAT]: 'pce_diary_invalid_date_format',
  [PCE.DIARY_DATE_IN_FUTURE]: 'pce_diary_date_in_future',
  [PCE.DIARY_DATE_TOO_OLD]: 'pce_diary_date_too_old',
  [PCE.DIARY_NOTES_TOO_LONG]: 'pce_diary_notes_too_long',
  [PCE.DIARY_INVALID_NOTES]: 'pce_diary_invalid_notes',
  [PCE.DIARY_INVALID_IMPACT]: 'pce_diary_invalid_impact',
  [PCE.DIARY_IMPACT_REQUIRED]: 'pce_diary_impact_required',
  [PCE.DIARY_IMPACT_NOT_ALLOWED]: 'pce_diary_impact_not_allowed',
  [PCE.DIARY_INVALID_MEDICATION]: 'pce_diary_invalid_medication',
  [PCE.DIARY_TRIPTAN_NOT_MIGRAINE]: 'pce_diary_triptan_not_migraine',
  [PCE.DIARY_PAINKILLER_NOT_ALLOWED]: 'pce_diary_painkiller_not_allowed',
}

function dateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

function computePeriodStats(entries: Record<string, DiaryEntry>, periodDays = 28): SymptomStats {
  const today = startOfDay(new Date())
  const stats = emptyStats()

  for (let i = 0; i < periodDays; i++) {
    const key = dateKey(subDays(today, i))
    const entry = entries[key]
    const cat = entry?.symptom || DEFAULT_SYMPTOM
    if (cat in stats) stats[cat]++
  }

  return stats
}

function computeWeekStats(entries: Record<string, DiaryEntry>): WeekStats {
  const today = startOfDay(new Date())
  let filled = 0
  for (let i = 0; i < 7; i++) {
    const key = dateKey(subDays(today, i))
    if (entries[key]) filled++
  }
  return { filled, total: 7 }
}

function checkMedicationOveruse(entries: Record<string, DiaryEntry>): MedicationWarning {
  const stats = { triptanDays: 0, painkillerDays: 0 }
  const today = startOfDay(new Date())

  for (let i = 0; i < 28; i++) {
    const key = dateKey(subDays(today, i))
    const entry = entries[key]
    if (!entry) continue
    if (entry.triptanUsed) stats.triptanDays++
    if (entry.painkillerUsed) stats.painkillerDays++
  }

  return {
    ...stats,
    triptanWarning: stats.triptanDays > 10,
    painkillerWarning: stats.painkillerDays > 15,
  }
}

function entriesToMap(entriesArray: Array<Record<string, unknown>>): Record<string, DiaryEntry> {
  const map: Record<string, DiaryEntry> = {}
  for (const entry of entriesArray) {
    map[entry.date as string] = { ...entry, symptom: fromDbValue(entry.symptom as string) } as DiaryEntry
  }
  return map
}

function computeMonthStats(entries: Record<string, DiaryEntry>, month: Date): MonthStats {
  const start = startOfMonth(month)
  const today = startOfDay(new Date())
  const stats = { ...emptyStats(), triptanDays: 0, painkillerDays: 0, totalImpact: 0, impactDays: 0 }
  for (let i = 0; i < 31; i++) {
    const d = addDays(start, i)
    if (format(d, 'yyyy-MM') !== format(start, 'yyyy-MM')) break
    if (isAfter(d, today)) break
    const key = dateKey(d)
    const entry = entries[key]
    const cat = entry?.symptom || DEFAULT_SYMPTOM
    if (cat in stats) stats[cat as SymptomKey]++
    if (entry?.triptanUsed) stats.triptanDays++
    if (entry?.painkillerUsed) stats.painkillerDays++
    if (entry?.impactScore) { stats.totalImpact += entry.impactScore; stats.impactDays++ }
  }
  return { ...stats, avgImpact: stats.impactDays > 0 ? Math.round((stats.totalImpact / stats.impactDays) * 10) / 10 : 0 }
}

function getAvailableSymptoms(triptanUsed: boolean, painkillerUsed: boolean): SymptomAvailability[] {
  const symptoms: SymptomAvailability[] = SYMPTOM_KEYS.map(key => ({ key, enabled: true, disabledReason: null }))
  const disable = (dbVal: string, reason: string) => {
    const i = symptoms.findIndex(s => s.key === fromDbValue(dbVal))
    if (i >= 0) symptoms[i] = { ...symptoms[i], enabled: false, disabledReason: reason }
  }
  if (triptanUsed) {
    disable('headache', 'disabledTriptanOnly')
    disable('brainfog', 'disabledTriptanOnly')
    disable('no-symptoms', 'disabledMedicationUsed')
  } else if (painkillerUsed) {
    disable('brainfog', 'disabledPainkillerNoFog')
    disable('no-symptoms', 'disabledMedicationUsed')
  }
  return symptoms
}

type DiaryFormState = Pick<
  DiaryState,
  'formSymptom' | 'formTriptanUsed' | 'formPainkillerUsed' | 'formImpactScore' | 'formNotes'
>

// De formuliervelden zoals ze voor een dag horen te staan: de opgeslagen entry, of leeg.
function formStateFor(entry: DiaryEntry | undefined): DiaryFormState {
  return {
    formSymptom: entry?.symptom || null,
    formTriptanUsed: entry?.triptanUsed || false,
    formPainkillerUsed: entry?.painkillerUsed || false,
    formImpactScore: entry?.impactScore || null,
    formNotes: entry?.notes || '',
  }
}

function recompute(entries: Record<string, DiaryEntry>): Pick<DiaryState, 'periodStats' | 'weekStats' | 'medicationWarning'> {
  return {
    periodStats: computePeriodStats(entries),
    weekStats: computeWeekStats(entries),
    medicationWarning: checkMedicationOveruse(entries),
  }
}

export type { MonthStats, SymptomAvailability, NavDirection, ActiveTab, WeekStats, MedicationWarning }
export { getAvailableSymptoms, computeMonthStats }

// Eén bron voor de begintoestand, zodat reset() niet uit de pas kan lopen.
// selectedDate/calendarMonth worden per aanroep bepaald: "vandaag" verschuift.
const initialState = (): DiaryData => ({
  entries: {},
  isLoading: false,
  isLoaded: false,
  loadFailed: false,
  selectedDate: startOfDay(new Date()),
  currentStep: 1,
  showSuccess: false,
  showCalendar: false,
  navDirection: null,
  activeTab: 'calendar',
  calendarMonth: startOfMonth(new Date()),
  calendarSelectedDay: null,
  // Wizard opent alleen op expliciete actie, nooit automatisch bij paginabezoek
  isEditing: false,
  saveError: null,
  isSavingEntry: false,
  ...formStateFor(undefined),
  periodStats: emptyStats(),
  weekStats: { filled: 0, total: 7 },
  medicationWarning: { triptanDays: 0, painkillerDays: 0, triptanWarning: false, painkillerWarning: false },
})

export const useDiaryStore = create<DiaryState>((set, get) => ({
  ...initialState(),

  fetchEntries: async () => {
    const { isLoaded, isLoading } = get()
    if (isLoaded || isLoading) return

    if (!useAuthStore.getState().isAuthenticated) return

    set({ isLoading: true, loadFailed: false })
    try {
      const { data } = await DiaryService.getEntries()
      const entries = entriesToMap(Array.isArray(data) ? data : [])
      logDev('diaryStore: fetched entries', Object.keys(entries).length)
      const { selectedDate } = get()
      const existing = entries[dateKey(selectedDate)]
      set({
        entries, isLoaded: true, isLoading: false, loadFailed: false,
        ...recompute(entries),
        ...formStateFor(existing),
      })
    } catch (e) {
      errorDev('diaryStore: fetch failed', e)
      // Report it, but keep the toast out: the page shows the error in place
      // with a retry, which beats a toast the patient can miss.
      notifyApiError(e, {
        t: i18next.t.bind(i18next),
        tags: { feature: 'diary', action: 'fetchEntries' },
      })
      set({
        entries: {},
        isLoading: false,
        isLoaded: false,
        loadFailed: true,
        ...recompute({}),
      })
    }
  },

  retryFetch: async () => {
    set({ loadFailed: false })
    await get().fetchEntries()
  },

  setSelectedDate: (date: Date) => {
    const d = startOfDay(date)
    const today = startOfDay(new Date())
    if (isAfter(d, today)) return

    const { selectedDate, entries } = get()
    const existing = entries[dateKey(d)]
    const dir = d < selectedDate ? 'prev' : 'next'

    set({
      selectedDate: d,
      navDirection: dir,
      currentStep: 1,
      showSuccess: false,
      isEditing: !existing,
      ...formStateFor(existing),
    })
  },

  navigateDay: (direction: NavDirection) => {
    const { selectedDate, entries } = get()
    const newDate = direction === 'prev'
      ? subDays(selectedDate, 1)
      : addDays(selectedDate, 1)

    const today = startOfDay(new Date())
    if (isAfter(newDate, today)) return

    const existing = entries[dateKey(newDate)]
    set({
      selectedDate: newDate,
      navDirection: direction,
      currentStep: 1,
      showSuccess: false,
      isEditing: !existing,
      ...formStateFor(existing),
    })
  },

  navigateWeek: (direction: NavDirection) => {
    const { selectedDate, entries } = get()
    const today = startOfDay(new Date())
    const weekOpts = { weekStartsOn: 1 as const }

    if (direction === 'next' && isSameWeek(selectedDate, today, weekOpts)) return

    const offset = direction === 'prev' ? -7 : 7
    let newDate = addDays(selectedDate, offset)

    // Clamp to today if we'd land in the future
    if (isAfter(newDate, today)) newDate = today

    const existing = entries[dateKey(newDate)]
    set({
      selectedDate: newDate,
      navDirection: direction,
      currentStep: 1,
      showSuccess: false,
      isEditing: !existing,
      ...formStateFor(existing),
    })
  },

  setFormSymptom: (symptom: SymptomKey) => {
    const dbVal = toDbValue(symptom)
    const updates: Partial<DiaryState> = { formSymptom: symptom }
    if (dbVal !== 'migraine') {
      updates.formTriptanUsed = false
    }
    if (dbVal !== 'migraine' && dbVal !== 'headache') {
      updates.formPainkillerUsed = false
    }
    set(updates as Partial<DiaryState>)
  },

  setFormTriptanUsed: (val: boolean) => set({ formTriptanUsed: val }),
  setFormPainkillerUsed: (val: boolean) => set({ formPainkillerUsed: val }),
  setFormImpactScore: (score: number | null) => set({ formImpactScore: score }),
  setFormNotes: (notes: string) => set({ formNotes: notes }),

  setCurrentStep: (step: number) => set({ currentStep: step }),
  setShowCalendar: (val: boolean) => set({ showCalendar: val }),

  // Tab & calendar actions
  setActiveTab: (tab: ActiveTab) => {
    const { showSuccess, entries, selectedDate } = get()
    if (showSuccess) {
      const existing = entries[dateKey(selectedDate)]
      set({
        activeTab: tab,
        showSuccess: false,
        currentStep: 1,
        isEditing: !existing,
        ...formStateFor(existing),
      })
    } else {
      set({ activeTab: tab })
    }
  },
  setCalendarMonth: (date: Date) => set({ calendarMonth: startOfMonth(date) }),
  navigateCalendarMonth: (direction: NavDirection) => {
    const { calendarMonth } = get()
    const today = startOfDay(new Date())
    if (direction === 'next' && isAfter(startOfMonth(addMonths(calendarMonth, 1)), today)) return
    const newMonth = direction === 'prev' ? subMonths(calendarMonth, 1) : addMonths(calendarMonth, 1)
    set({ calendarMonth: startOfMonth(newMonth) })
  },
  selectCalendarDay: (date: Date) => {
    const d = startOfDay(date)
    const today = startOfDay(new Date())
    if (isAfter(d, today)) return
    const { entries } = get()
    const existing = entries[dateKey(d)]
    set({
      calendarSelectedDay: d,
      selectedDate: d,
      currentStep: 1,
      showSuccess: false,
      isEditing: false,
      ...formStateFor(existing),
    })
  },

  saveEntry: async () => {
    const {
      selectedDate,
      formSymptom,
      formTriptanUsed,
      formPainkillerUsed,
      formImpactScore,
      formNotes,
      entries,
    } = get()

    const key = dateKey(selectedDate)
    const now = new Date().toISOString()
    const existing = entries[key]

    const entry: DiaryEntry = {
      date: key,
      symptom: formSymptom as SymptomKey,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    }

    const dbVal = toDbValue(formSymptom as SymptomKey)
    if (dbVal === 'migraine') {
      entry.triptanUsed = formTriptanUsed
      entry.painkillerUsed = formPainkillerUsed
    } else if (dbVal === 'headache') {
      entry.painkillerUsed = formPainkillerUsed
    }

    if (formSymptom !== DEFAULT_SYMPTOM && formImpactScore) {
      entry.impactScore = formImpactScore
    }

    if (formNotes?.trim()) {
      entry.notes = formNotes.trim()
    }

    // Optimistic update — save and go back to calendar
    const updatedEntries = { ...entries, [key]: entry }
    set({
      entries: updatedEntries,
      showSuccess: false,
      isEditing: false,
      currentStep: 1,
      saveError: null,
      isSavingEntry: true,
      ...recompute(updatedEntries),
    })

    // Persist to backend — convert frontend key → db value
    const { symptom: _sym, ...entryRest } = entry
    const apiEntry = { ...entryRest, symptom: toDbValue(formSymptom as SymptomKey) }
    if (useAuthStore.getState().isAuthenticated) {
      try {
        await DiaryService.saveEntry(apiEntry)
        logDev('diaryStore: saved entry to backend', key)
      } catch (e) {
        errorDev('diaryStore: save failed, reverting', e)
        // The returned message is the specific one (per PCE where the backend
        // gave a reason), so the banner says what went wrong instead of
        // "opslaan mislukt". No toast: the banner sits on this same page.
        const message = notifyApiError(e, {
          t: i18next.t.bind(i18next),
          overrides: PCE_MAPPING,
          tags: { feature: 'diary', action: 'saveEntry' },
        })
        // Revert on failure
        const reverted = { ...get().entries }
        if (existing) {
          reverted[key] = existing
        } else {
          delete reverted[key]
        }
        set({
          entries: reverted,
          showSuccess: false,
          saveError: message || i18next.t('diary:saveError'),
          ...recompute(reverted),
        })
      } finally {
        set({ isSavingEntry: false })
      }
    } else {
      set({ isSavingEntry: false })
    }
  },

  saveNotes: async (notes: string) => {
    const { selectedDate, entries } = get()
    const key = dateKey(selectedDate)
    const existing = entries[key]
    if (!existing) return

    const updatedEntry: DiaryEntry = { ...existing, notes: notes.trim(), updatedAt: new Date().toISOString() }
    const updatedEntries = { ...entries, [key]: updatedEntry }
    set({ entries: updatedEntries })

    if (useAuthStore.getState().isAuthenticated) {
      try {
        const { symptom: _notesSymptom, ...noteRest } = updatedEntry
        const apiEntry = { ...noteRest, symptom: toDbValue(updatedEntry.symptom) }
        await DiaryService.saveEntry(apiEntry)
        logDev('diaryStore: saved notes to backend', key)
      } catch (e) {
        errorDev('diaryStore: save notes failed, reverting', e)
        notifyApiError(e, {
          t: i18next.t.bind(i18next),
          showMessage: useMessageStore.getState().showMessage,
          overrides: PCE_MAPPING,
          tags: { feature: 'diary', action: 'saveNotes' },
        })
        set({ entries: { ...get().entries, [key]: existing } })
      }
    }
  },

  cancelEditing: () => {
    const { selectedDate, entries } = get()
    const key = format(selectedDate, 'yyyy-MM-dd')
    const existing = entries[key]
    set({
      isEditing: false,
      currentStep: 1,
      showSuccess: false,
      ...formStateFor(existing),
    })
  },

  startEditing: () => {
    set({
      activeTab: 'calendar',
      isEditing: true,
      currentStep: 1,
      showSuccess: false,
      saveError: null,
    })
  },

  reset: () => set(initialState()),

  resetForm: () => {
    const { selectedDate, entries } = get()
    const existing = entries[dateKey(selectedDate)]
    set({
      currentStep: 1,
      showSuccess: false,
      isEditing: !existing,
      ...formStateFor(existing),
    })
  },

  getEntryForDate: (date: Date) => {
    const { entries } = get()
    return entries[dateKey(date)] || null
  },

  isDateFilled: (date: Date) => {
    const { entries } = get()
    return !!entries[dateKey(date)]
  },

  isFutureDate: (date: Date) => {
    return isAfter(startOfDay(date), startOfDay(new Date()))
  },

  isSelectedDateToday: () => {
    return isToday(get().selectedDate)
  },
}))
